import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import AutoGrowTextarea from './components/AutoGrowTextarea';
import BackButton from '../../modules/layout/components/BackButton';
import Loader from '../../modules/layout/components/Loader';
import BaseSelect from '../../modules/layout/components/Select/BaseSelect';
import { useToast } from '../../modules/layout/components/ToastContext';
import CompareView from './components/CompareView';
import PayloadView from './components/PayloadView';
import StatsPanel from './components/StatsPanel';
import StructuralDiffSummary from './components/StructuralDiffSummary';
import TemplateModal from './components/TemplateModal';
import {
  getExecution,
  getExecutions,
  getParameters,
  getStatistics,
  getTemplates,
} from './transactionExplorerService';
import {
  ITransactionExecution,
  ITransactionExecutionSummary,
  ITransactionParameter,
  ITransactionStatistics,
  ITransactionTemplate,
  TemplateDirection,
  TransactionStatus,
} from './types';
import { ParamTypeText, ParamValueTypeText } from '../../helpers/EnumeratorHelper';
import { formatDateTime } from '../../helpers/DateTimeHelper';

const paramTypeText = ParamTypeText as unknown as Record<number, string>;
const paramValueTypeText = ParamValueTypeText as unknown as Record<number, string>;

const EXEC_FETCH_LIMIT = 30;
const EXEC_PAGE_SIZE = 10;
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const statusBadge = (status: TransactionStatus) => {
  switch (status) {
    case TransactionStatus.Success:
      return 'bg-success';
    case TransactionStatus.Failed:
      return 'bg-danger';
    default:
      return 'bg-secondary';
  }
};

const TransactionExplorerDetails = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { className: rawClassName } = useParams();
  const className = rawClassName ? decodeURIComponent(rawClassName) : '';

  const [days, setDays] = useState(30);
  const [stats, setStats] = useState<ITransactionStatistics | null>(null);
  const [templates, setTemplates] = useState<ITransactionTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [parameters, setParameters] = useState<ITransactionParameter[]>([]);
  const [executions, setExecutions] = useState<ITransactionExecutionSummary[]>([]);
  const [execPage, setExecPage] = useState(1);
  const [searchCorrelationId, setSearchCorrelationId] = useState('');
  const [selected, setSelected] = useState<ITransactionExecution | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [compareDirection, setCompareDirection] = useState<TemplateDirection>(TemplateDirection.Request);
  const [compareRankIndex, setCompareRankIndex] = useState(0);
  const [candidate, setCandidate] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    getStatistics(className, days)
      .then((s) => active && setStats(s))
      .catch((e) => showToast(t('ConnectionError', { error: String(e) }), 'error'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [className, days]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const tpls = await getTemplates(className);
      setTemplates(tpls);
    } catch {
      // non-fatal: templates are optional
    } finally {
      setTemplatesLoading(false);
    }
  }, [className]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    let active = true;
    getParameters(className)
      .then((p) => active && setParameters(p))
      .catch(() => undefined);
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [className]);

  useEffect(() => {
    let active = true;
    setExecPage(1);
    getExecutions(className, EXEC_FETCH_LIMIT)
      .then((list) => active && setExecutions(list))
      .catch(() => undefined);
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [className]);

  const handleSelectExecution = async (correlationId: string) => {
    try {
      const detail = await getExecution(correlationId);
      setSelected(detail);
    } catch (e) {
      showToast(t('ConnectionError', { error: String(e) }), 'error');
    }
  };

  const handleFindByCorrelationId = async () => {
    const id = searchCorrelationId.trim();
    if (!id) return;
    if (!GUID_REGEX.test(id)) {
      showToast(t('CorrelationIdInvalidFormat'), 'warning');
      return;
    }
    try {
      const detail = await getExecution(id);
      if (!detail) {
        showToast(t('TransactionNotFound'), 'warning');
        return;
      }
      setSelected(detail);
    } catch {
      showToast(t('TransactionNotFound'), 'warning');
    }
  };

  const directionTemplates = templates
    .filter((tpl) => tpl.direction === compareDirection)
    .sort((a, b) => a.rank - b.rank);

  const selectedTemplate = directionTemplates[compareRankIndex] ?? directionTemplates[0];
  const templateBody = selectedTemplate?.templateBody ?? undefined;

  const windowOptions = [
    { value: 7, label: t('LastNDays', { count: 7 }) },
    { value: 30, label: t('LastNDays', { count: 30 }) },
    { value: 90, label: t('LastNDays', { count: 90 }) },
    { value: 0, label: t('AllTime') },
  ];

  const execTotalPages = Math.max(1, Math.ceil(executions.length / EXEC_PAGE_SIZE));
  const pagedExecutions = executions.slice((execPage - 1) * EXEC_PAGE_SIZE, execPage * EXEC_PAGE_SIZE);

  return (
    <div className="gg-transactionExplorerDetails container mt-4">
      <AppBreadcrumb
        items={[
          { title: t('Home'), href: '/' },
          { title: t('TransactionExplorer'), href: '/transaction-explorer' },
          { title: className },
        ]}
      />

      <div className="d-flex justify-content-between align-items-end mb-3 flex-wrap gap-2">
        <div>
          <div className="gg-tx-eyebrow">{t('Transaction')}</div>
          <h3 className="mb-0 gg-tx-title">{className}</h3>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <label className="small text-muted mb-0">{t('Window')}</label>
          <div style={{ minWidth: 150 }}>
            <BaseSelect
              options={windowOptions}
              value={windowOptions.find((o) => o.value === days) || null}
              onChange={(opt: any) => setDays(Number(opt?.value ?? 30))}
              isClearable={false}
              isSearchable={false}
            />
          </div>
          <button type="button" className="btn gg-btn gg-btn-submit btn-sm" onClick={() => setShowTemplates(true)}>
            {t('RequestResponseTemplate')}
          </button>
        </div>
      </div>

      {loading && <Loader />}

      {stats && (
        <div className="mb-4">
          <StatsPanel stats={stats} />
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>{t('TransactionParameters')}</span>
          {parameters.length > 0 && <span className="badge bg-secondary">{parameters.length}</span>}
        </div>
        <div className="card-body p-0">
          {parameters.length === 0 ? (
            <div className="text-muted fst-italic p-3 mb-0">{t('NoParameters')}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm gg-params-table mb-0">
                <thead>
                  <tr>
                    <th>{t('ParameterName')}</th>
                    <th>{t('ParameterValue')}</th>
                    <th>{t('ParameterType')}</th>
                    <th>{t('ParameterValueType')}</th>
                  </tr>
                </thead>
                <tbody>
                  {parameters.map((p, i) => (
                    <tr key={p.id ?? i}>
                      <td className="fw-semibold text-nowrap">{p.paramName}</td>
                      <td className="gg-mono text-break">{p.paramValue}</td>
                      <td className="text-nowrap">{paramTypeText[p.paramType] ?? p.paramType}</td>
                      <td className="text-nowrap">{paramValueTypeText[p.paramValueType] ?? p.paramValueType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-lg-5 mb-3">
          <div className="card h-100">
            <div className="card-header">{t('RecentExecutions')}</div>
            <div className="p-2 border-bottom">
              <div className="input-group input-group-sm">
                <input
                  className="form-control"
                  placeholder={t('FindByCorrelationId')}
                  value={searchCorrelationId}
                  onChange={(e) => setSearchCorrelationId(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleFindByCorrelationId();
                  }}
                />
                <button type="button" className="btn gg-btn-black" onClick={handleFindByCorrelationId}>
                  {t('Find')}
                </button>
              </div>
            </div>
            <div className="table-responsive" style={{ maxHeight: 360, overflow: 'auto' }}>
              <table className="table table-sm table-hover small mb-0 gg-exec-table">
                <thead>
                  <tr>
                    <th>{t('StartedAt')}</th>
                    <th>{t('Status')}</th>
                    <th className="text-end">{t('Duration')}</th>
                    <th>{t('Caller')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedExecutions.map((ex) => (
                    <tr
                      key={ex.correlationId}
                      role="button"
                      className={selected?.correlationId === ex.correlationId ? 'gg-exec-row-selected' : ''}
                      onClick={() => handleSelectExecution(ex.correlationId)}
                    >
                      <td className="text-nowrap">
                        {formatDateTime(ex.startedAt)}
                      </td>
                      <td>
                        <span className={`badge ${statusBadge(ex.status)}`}>
                          {ex.status === TransactionStatus.Failed ? t('Failed') : t('Success')}
                        </span>
                      </td>
                      <td className="text-end">{ex.durationMs != null ? `${ex.durationMs} ms` : ''}</td>
                      <td className="text-nowrap">{ex.caller}</td>
                    </tr>
                  ))}
                  {executions.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-3">
                        {t('NoResults')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {executions.length > EXEC_PAGE_SIZE && (
              <div className="card-footer d-flex justify-content-between align-items-center py-2 small bg-transparent">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={execPage <= 1}
                  onClick={() => setExecPage((p) => Math.max(1, p - 1))}
                >
                  {t('Previous')}
                </button>
                <span className="text-muted">
                  {t('Page')} {execPage} / {execTotalPages} · {t('Total')}: {executions.length}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={execPage >= execTotalPages}
                  onClick={() => setExecPage((p) => Math.min(execTotalPages, p + 1))}
                >
                  {t('Next')}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-7 mb-3">
          <div className="card h-100">
            <div className="card-header">{t('ExecutionDetails')}</div>
            <div className="card-body">
              {!selected ? (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-arrow-left-circle fs-3 d-block mb-2 opacity-50" />
                  {t('SelectExecutionHint')}
                </div>
              ) : (
                <>
                  <div className="gg-meta-grid mb-4">
                    <div>
                      <div className="gg-meta-label">{t('CorrelationId')}</div>
                      <div className="gg-meta-value gg-mono">{selected.correlationId}</div>
                    </div>
                    <div>
                      <div className="gg-meta-label">{t('Status')}</div>
                      <div className="gg-meta-value">
                        <span className={`badge ${statusBadge(selected.status)}`}>
                          {selected.status === TransactionStatus.Failed ? t('Failed') : t('Success')}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="gg-meta-label">{t('StartedAt')}</div>
                      <div className="gg-meta-value">{formatDateTime(selected.startedAt)}</div>
                    </div>
                    <div>
                      <div className="gg-meta-label">{t('Duration')}</div>
                      <div className="gg-meta-value">
                        {selected.durationMs != null ? `${selected.durationMs} ms` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="gg-meta-label">{t('Caller')}</div>
                      <div className="gg-meta-value">{selected.caller || '—'}</div>
                    </div>
                    {selected.impersonator && (
                      <div>
                        <div className="gg-meta-label">{t('Impersonator')}</div>
                        <div className="gg-meta-value">{selected.impersonator}</div>
                      </div>
                    )}
                  </div>

                  <div className="gg-section-title">{t('Parameters')}</div>
                  {selected.parameters.length === 0 ? (
                    <div className="text-muted fst-italic mb-3">{t('NoParameters')}</div>
                  ) : (
                    <table className="table table-sm table-bordered small mb-3 gg-params-table">
                      <tbody>
                        {selected.parameters.map((p, i) => (
                          <tr key={i}>
                            <th style={{ width: '40%' }}>{p.key}</th>
                            <td>{p.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <div className="row">
          <div className="col-lg-6 mb-3">
            <div className="gg-section-title">{t('Request')}</div>
            <PayloadView body={selected.requestBody} maxHeight={420} />
          </div>
          <div className="col-lg-6 mb-3">
            <div className="gg-section-title">{t('Response')}</div>
            <PayloadView body={selected.responseBody} maxHeight={420} />
          </div>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>{t('CompareWithTemplate')}</span>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className={`btn ${compareDirection === TemplateDirection.Request ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => {
                  setCompareDirection(TemplateDirection.Request);
                  setCompareRankIndex(0);
                }}
              >
                {t('Request')}
              </button>
              <button
                type="button"
                className={`btn ${compareDirection === TemplateDirection.Response ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => {
                  setCompareDirection(TemplateDirection.Response);
                  setCompareRankIndex(0);
                }}
              >
                {t('Response')}
              </button>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-dark"
              disabled={!selected}
              onClick={() =>
                setCandidate(
                  (compareDirection === TemplateDirection.Request
                    ? selected?.requestBody
                    : selected?.responseBody) ?? ''
                )
              }
            >
              {t('LoadFromExecution')}
            </button>
            {candidate && (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setCandidate('')}>
                {t('Clear')}
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          {directionTemplates.length > 1 && (
            <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
              <span className="small text-muted">{t('TemplateVariant')}:</span>
              <div className="btn-group btn-group-sm" role="group">
                {directionTemplates.map((tpl, i) => (
                  <button
                    key={tpl.rank}
                    type="button"
                    className={`btn ${i === compareRankIndex ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    onClick={() => setCompareRankIndex(i)}
                    title={t('SeenTimes', { count: tpl.occurrenceCount })}
                  >
                    {tpl.isPinned && <i className="bi bi-pin-angle-fill me-1" />}
                    {t('TemplateRank', { rank: tpl.rank })} ({tpl.occurrenceCount})
                  </button>
                ))}
              </div>
            </div>
          )}
          <label className="form-label small text-muted">{t('PastePayloadLabel')}</label>
          <AutoGrowTextarea
            className="form-control font-monospace mb-3"
            minRows={6}
            value={candidate}
            onChange={(e) => setCandidate(e.target.value)}
            placeholder={t('PastePayloadPlaceholder')}
          />
          {candidate.trim() ? (
            templateBody ? (
              <>
                <StructuralDiffSummary candidate={candidate} template={templateBody} />
                <CompareView
                  leftLabel={t('YourPayload')}
                  rightLabel={`${compareDirection === TemplateDirection.Request ? t('Request') : t('Response')} ${t('Template')}`}
                  left={candidate}
                  right={templateBody}
                />
              </>
            ) : (
              <div className="text-muted fst-italic">{t('NoTemplateAvailable')}</div>
            )
          ) : (
            <div className="text-center text-muted py-4">
              <i className="bi bi-clipboard-check fs-4 d-block mb-2 opacity-50" />
              {t('PasteToCompare')}
            </div>
          )}
        </div>
      </div>

      <div className="mb-4">
        <BackButton goTo="/transaction-explorer" />
      </div>

      <TemplateModal
        show={showTemplates}
        className={className}
        templates={templates}
        loading={templatesLoading}
        onClose={() => setShowTemplates(false)}
        onTemplatesChanged={loadTemplates}
      />
    </div>
  );
};

export default TransactionExplorerDetails;
